import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../firebase/config';
import { ENTITY_INTAKE_SECTIONS, getVisibleFieldKeysForStep, type EntityIntakeFieldKey } from '../../lib/intake/entityFormationFields';
import { ENTITY_INTAKE_DEFAULTS, createEmptyOwner, type EntityIntakeDefaults, type Owner } from '../../lib/intake/entityFormationDefaults';
import { validateStep, validateAll } from '../../lib/intake/entityFormationValidation';
import { useEntityFormationDraft } from '../../hooks/useEntityFormationDraft';
import { submitEntityFormationIntake, deleteEntityFormationDraft } from '../../lib/intake/entityFormationSubmitApi';
import { US_STATES } from '../../lib/usStates';
import { TextField } from '../forms/TextField';
import { TextAreaField } from '../forms/TextAreaField';
import { RadioGroupField } from '../forms/RadioGroupField';
import { CheckboxGroupField } from '../forms/CheckboxGroupField';
import { SelectField } from '../forms/SelectField';
import { MultiSelectField } from '../forms/MultiSelectField';
import {
  OWNER_TYPE_OPTIONS,
  YES_NO_OPTIONS,
  type Option,
} from '../../lib/intake/entityFormationOptions';

/**
 * Gets the human-readable label for a field
 */
function getFieldLabel(fieldKey: EntityIntakeFieldKey): string {
  const labels: Record<string, string> = {
    full_legal_name: 'Full Legal Name',
    preferred_name: 'Preferred Name',
    phone_number: 'Phone Number',
    email_address: 'Email Address',
    street_address: 'Street Address 1',
    street_address_2: 'Street Address 2',
    city: 'City',
    state: 'State',
    zip_code: 'ZIP Code',
    state_of_residence: 'State of Residence',
    us_citizen: 'US Citizen',
    citizenship_status: 'Citizenship Status',
    ssn_itin: 'SSN/ITIN (Optional)',
    legal_business_name: 'Legal Business Name',
    alternate_business_names: 'Alternate Business Names',
    business_description: 'Business Description',
    industry: 'Industry / Sector',
    industry_other: 'Other Industry',
    timeline: 'Timeline',
    revenue_expectations: '1st Year Revenue Expectations',
    growth_outlook: 'Growth Outlook',
    strategic_intent: 'Strategic Intent',
    strategic_intent_other: 'Other Strategic Intent',
    number_of_owners: 'Number of Owners',
    owners: 'Owners',
    management_preference: 'Management Preference',
    future_ownership_changes: 'Future Ownership Changes',
    operating_states: 'Operating States',
    operates_outside_formation_state: 'Operates Outside Formation State',
    has_employees: 'Has Employees',
    uses_contractors: 'Uses 1099 Contractors',
    sells_online: 'Sells Online',
    serves_nationally: 'Serves Nationally',
    risk_factors: 'Risk Factors',
    liability_risk: 'Liability Risk',
    has_business_bank_account: 'Has Business Bank Account',
    uses_bookkeeping_software: 'Uses Bookkeeping Software',
    bookkeeping_software_name: 'Bookkeeping Software Name',
    discussed_tax_planning: 'Discussed Tax Planning',
    owns_domain: 'Owns Domain',
    domain_name: 'Domain Name',
    website_timeline: 'Website Timeline',
    interested_in_brand_protection: 'Interested in Brand Protection',
    initial_funding: 'Initial Funding',
    initial_funding_other: 'Other Initial Funding',
    plans_to_raise_capital: 'Plans to Raise Capital',
    additional_services: 'Additional Services Interest',
    acknowledge_not_attorney_or_cpa: 'I understand that Help Keep My Money LLC provides business management consulting, is not acting as my attorney or CPA, and is not licensed to provide legal or financial advice.',
    acknowledge_entity_formation_scope: 'I understand entity formation is only one part of building a compliant and sustainable business.',
    certify_accuracy: 'I confirm the information provided is accurate to the best of my knowledge.',
    client_signature: 'Client Signature (must match Client Full Legal Name)',
    signature_date: 'Signature Date',
  };

  return labels[fieldKey] || fieldKey.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Gets the minimum number of owners to display based on number_of_owners field
 */
function getMinOwners(numberOfOwners: string | null): number {
  if (numberOfOwners === '1') return 1;
  if (numberOfOwners === '2_3') return 2;
  if (numberOfOwners === '4_plus') return 4;
  return 1;
}

/**
 * Gets the maximum number of owners allowed based on number_of_owners field
 */
function getMaxOwners(numberOfOwners: string | null): number {
  if (numberOfOwners === '1') return 1;
  if (numberOfOwners === '2_3') return 3;
  if (numberOfOwners === '4_plus') return 10;
  return 10;
}

/**
 * Gets the number of visible owners to display (minimum based on number_of_owners selection)
 */
function getVisibleOwnerCount(numberOfOwners: string | null): number {
  return getMinOwners(numberOfOwners);
}

/**
 * Convert US_STATES to Option format
 */
const STATE_OPTIONS: Option[] = US_STATES.map((state) => ({
  value: state.code,
  label: state.name,
}));

/**
 * Operating states options with "Nationwide" as first option
 */
const OPERATING_STATES_OPTIONS: Option[] = [
  { value: 'nationwide', label: 'Nationwide' },
  ...STATE_OPTIONS,
];

/**
 * Helper function to safely get options from a section
 */
function getSectionOptions(section: typeof ENTITY_INTAKE_SECTIONS[number], fieldKey: string): readonly Option[] | undefined {
  if ('options' in section && section.options) {
    const options = section.options as Record<string, readonly Option[]>;
    return options[fieldKey];
  }
  return undefined;
}

/**
 * Main wizard component for entity formation intake form
 */
interface EntityFormationWizardProps {
  specificDraftId?: string | null;
}

export const EntityFormationWizard: React.FC<EntityFormationWizardProps> = ({ specificDraftId }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<EntityIntakeDefaults>(ENTITY_INTAKE_DEFAULTS);
  const [currentStep, setCurrentStep] = useState(1);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get user info from auth
  const user = auth.currentUser;
  const uid = user?.uid || null;
  const email = user?.email || null;

  // Use draft hook for autosave and draft loading (only if authenticated)
  const { isLoading: isDraftLoading, draftId, saveNow } = useEntityFormationDraft(formData, setFormData, specificDraftId);

  const totalRegularSteps = ENTITY_INTAKE_SECTIONS.length; // 11 steps
  const isReviewStep = currentStep > totalRegularSteps;

  // Ensure minimum owners exist when number_of_owners changes
  // This hook must be called before any early returns
  useEffect(() => {
    if (!uid || !email) return; // Early exit if not authenticated
    
    const minOwners = getMinOwners(formData.number_of_owners);
    if (formData.owners.length < minOwners) {
      setFormData((prev) => {
        const newOwners = [...prev.owners];
        while (newOwners.length < minOwners) {
          newOwners.push(createEmptyOwner());
        }
        return {
          ...prev,
          owners: newOwners,
        };
      });
    }
  }, [formData.number_of_owners, formData.owners.length, uid, email]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Helper function to convert YYYY-MM-DD to MM-DD-YYYY
  const formatDateToMMDDYYYY = (dateStr: string): string => {
    if (!dateStr || dateStr.trim() === '') return '';
    // Check if already in MM-DD-YYYY format
    if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) return dateStr;
    // Convert from YYYY-MM-DD to MM-DD-YYYY
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return dateStr;
  };

  // Helper function to convert MM-DD-YYYY to YYYY-MM-DD (for HTML date input)
  const formatDateToYYYYMMDD = (dateStr: string): string => {
    if (!dateStr || dateStr.trim() === '') return '';
    // Check if already in YYYY-MM-DD format
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
    // Convert from MM-DD-YYYY to YYYY-MM-DD
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[0]}-${parts[1]}`;
    }
    return dateStr;
  };

  // Autofill signature_date with today's date when step 11 is reached (formatted as MM-DD-YYYY)
  useEffect(() => {
    if (!uid || !email) return; // Early exit if not authenticated
    if (currentStep !== 11) return; // Only run on step 11

    setFormData((prev) => {
      // Only autofill if signature_date is empty
      if (!prev.signature_date || prev.signature_date.trim() === '') {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        // Store as MM-DD-YYYY
        const formattedDate = `${month}-${day}-${year}`;
        return {
          ...prev,
          signature_date: formattedDate,
        };
      }
      return prev;
    });
  }, [currentStep, uid, email]);

  // Autofill Owner 1 with client information when step 4 is reached
  useEffect(() => {
    if (!uid || !email) return; // Early exit if not authenticated
    if (currentStep !== 4) return; // Only run on step 4

    setFormData((prev) => {
      // Ensure owners array has at least one owner
      if (prev.owners.length === 0) {
        return {
          ...prev,
          owners: [createEmptyOwner()],
        };
      }

      // Check if Owner 1 is empty (check full_legal_name as indicator)
      const owner1 = prev.owners[0];
      if (!owner1.full_legal_name.trim()) {
        // Autofill Owner 1 with client information from step 1
        const newOwners = [...prev.owners];
        newOwners[0] = {
          ...newOwners[0],
          full_legal_name: prev.full_legal_name,
          phone_number: prev.phone_number,
          email_address: prev.email_address,
          street_address: prev.street_address,
          street_address_2: prev.street_address_2,
          city: prev.city,
          state: prev.state,
          zip_code: prev.zip_code,
          state_of_residence: prev.state_of_residence,
          us_citizen: prev.us_citizen,
          citizenship_status: prev.citizenship_status,
          // Note: ownership_percentage and owner_type remain unchanged (empty/null)
        };
        return {
          ...prev,
          owners: newOwners,
        };
      }

      return prev; // No changes needed
    });
  }, [currentStep, uid, email]);

  // Redirect or show message if not authenticated
  if (!uid || !email) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', textAlign: 'center' }}>
        <h2>Sign In Required</h2>
        <p>You must be signed in to access the Entity Formation Intake form.</p>
        <button
          onClick={() => navigate('/sign-in')}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          Go to Sign In
        </button>
      </div>
    );
  }

  const handleFieldChange = (fieldKey: EntityIntakeFieldKey, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
    // Clear validation error for this field
    if (validationErrors[fieldKey]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  const handleOwnerFieldChange = (ownerIndex: number, fieldKey: keyof Owner, value: any) => {
    setFormData((prev) => {
      const newOwners = [...prev.owners];
      newOwners[ownerIndex] = {
        ...newOwners[ownerIndex],
        [fieldKey]: value,
      };
      return {
        ...prev,
        owners: newOwners,
      };
    });
  };

  const addOwner = () => {
    const maxOwners = getMaxOwners(formData.number_of_owners);
    if (formData.owners.length < maxOwners) {
      setFormData((prev) => ({
        ...prev,
        owners: [...prev.owners, createEmptyOwner()],
      }));
    }
  };

  const removeOwner = (index: number) => {
    const minOwners = getMinOwners(formData.number_of_owners);
    // Only allow removal if we have more than the minimum required
    if (formData.owners.length > minOwners) {
      setFormData((prev) => ({
        ...prev,
        owners: prev.owners.filter((_, i) => i !== index),
      }));
    }
  };

  const validateCurrentStep = (): boolean => {
    if (isReviewStep) {
      return true; // Review step doesn't need validation
    }

    // Get visible fields for this step
    const visibleFieldKeys = getVisibleFieldKeysForStep(currentStep, formData);
    
    // Validate only visible fields for this step
    const errors = validateStep(currentStep, formData, visibleFieldKeys);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (validateCurrentStep()) {
      // Save draft on step change
      try {
        await saveNow();
      } catch (error) {
        console.error('Error saving draft on step change:', error);
        // Continue even if save fails
      }

      if (currentStep <= totalRegularSteps) {
        setCurrentStep(currentStep + 1);
        setValidationErrors({});
      }
    }
  };

  const handleSubmit = async () => {
    if (!uid || !email) {
      return;
    }

    // Validate all fields
    const errors = validateAll(formData);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.querySelector(`[name="${firstErrorField}"]`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsSubmitting(true);
    try {
      // Submit the form
      const result = await submitEntityFormationIntake({
        uid,
        email,
        data: formData,
      });

      // Optionally delete draft after successful submit
      if (draftId) {
        try {
          await deleteEntityFormationDraft(draftId);
        } catch (error) {
          console.error('Error deleting draft after submission:', error);
          // Continue even if draft deletion fails
        }
      }

      // Navigate to complete page
      navigate(`/intake/entity-formation/complete?submissionId=${result.submissionId}`);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit form. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setValidationErrors({});
    }
  };

  const getErrorForField = (fieldKey: string): string | undefined => {
    return validationErrors[fieldKey];
  };

  const renderField = (fieldKey: EntityIntakeFieldKey, sectionOptions?: Record<string, readonly Option[]>) => {
    const options = sectionOptions?.[fieldKey];
    const error = getErrorForField(fieldKey);
    const value = formData[fieldKey];
    const label = getFieldLabel(fieldKey);

    // Handle different field types
    if (fieldKey === 'state' || fieldKey === 'state_of_residence') {
      return (
        <SelectField
          key={fieldKey}
          label={label}
          value={value as string | null}
          onChange={(val) => handleFieldChange(fieldKey, val)}
          options={STATE_OPTIONS}
          error={error}
          placeholder="Select a state"
        />
      );
    }

    if (fieldKey === 'operating_states') {
      const operatingStatesValue = (value as string[]) || [];
      const isNationwideSelected = operatingStatesValue.includes('nationwide');
      
      return (
        <MultiSelectField
          key={fieldKey}
          label={label}
          value={operatingStatesValue}
          onChange={(val) => {
            // If "Nationwide" is being selected, clear all other selections
            if (val.includes('nationwide') && !operatingStatesValue.includes('nationwide')) {
              handleFieldChange(fieldKey, ['nationwide']);
            } else if (operatingStatesValue.includes('nationwide') && !val.includes('nationwide')) {
              // If "Nationwide" is being deselected, allow other selections
              handleFieldChange(fieldKey, val);
            } else if (!val.includes('nationwide')) {
              // Normal selection/deselection when "Nationwide" is not involved
              handleFieldChange(fieldKey, val);
            }
          }}
          options={OPERATING_STATES_OPTIONS}
          error={error}
          placeholder="Select operating states"
          isOptionDisabled={(optionValue) => isNationwideSelected && optionValue !== 'nationwide'}
        />
      );
    }

    if (fieldKey === 'business_description') {
      return (
        <TextAreaField
          key={fieldKey}
          label={label}
          value={(value as string) || ''}
          onChange={(val) => handleFieldChange(fieldKey, val)}
          error={error}
          rows={4}
        />
      );
    }

    if (fieldKey.includes('email')) {
      return (
        <TextField
          key={fieldKey}
          label={label}
          value={(value as string) || ''}
          onChange={(val) => handleFieldChange(fieldKey, val)}
          type="email"
          error={error}
        />
      );
    }

    if (fieldKey.includes('phone')) {
      return (
        <TextField
          key={fieldKey}
          label={label}
          value={(value as string) || ''}
          onChange={(val) => handleFieldChange(fieldKey, val)}
          type="tel"
          error={error}
        />
      );
    }

    // US Citizen field - Yes/No radio group
    if (fieldKey === 'us_citizen') {
      return (
        <RadioGroupField
          key={fieldKey}
          label={label}
          value={(value as string | null) || null}
          onChange={(val) => handleFieldChange(fieldKey, val)}
          options={YES_NO_OPTIONS}
          error={error}
        />
      );
    }

    // SSN/ITIN field - Special handling with explanation (masked for security)
    if (fieldKey === 'ssn_itin') {
      return (
        <div key={fieldKey}>
          <TextField
            label={label}
            value={(value as string) || ''}
            onChange={(val) => handleFieldChange(fieldKey, val)}
            type="password"
            error={error}
            placeholder="XXX-XX-XXXX"
          />
          <div style={{
            marginTop: '8px',
            padding: '12px',
            backgroundColor: '#e7f3ff',
            border: '1px solid #007bff',
            borderRadius: '4px',
            fontSize: '0.875rem',
            color: '#004085',
            lineHeight: '1.5'
          }}>
            <strong>Privacy Notice:</strong> Help Keep My Money collects Social Security Numbers and Individual Taxpayer Identification Numbers solely for the purpose of entity formation and required filings. This information is not retained beyond the time necessary to complete such filings and is securely deleted thereafter.
          </div>
        </div>
      );
    }

    // Multi-select checkbox groups
    const checkboxGroupFields = ['industry', 'strategic_intent', 'risk_factors', 'initial_funding', 'additional_services'];
    if (checkboxGroupFields.includes(fieldKey) && options) {
      return (
        <CheckboxGroupField
          key={fieldKey}
          label={label}
          value={(value as string[]) || []}
          onChange={(val) => handleFieldChange(fieldKey, val)}
          options={options}
          error={error}
        />
      );
    }

    // Radio groups (single select with options)
    if (options && options.length > 0) {
      return (
        <RadioGroupField
          key={fieldKey}
          label={label}
          value={(value as string | null) || null}
          onChange={(val) => handleFieldChange(fieldKey, val)}
          options={options}
          error={error}
        />
      );
    }

    // Signature date field - date input (convert between MM-DD-YYYY storage and YYYY-MM-DD HTML format)
    if (fieldKey === 'signature_date') {
      const dateValue = (value as string) || '';
      // Convert MM-DD-YYYY to YYYY-MM-DD for HTML date input
      const htmlDateValue = formatDateToYYYYMMDD(dateValue);
      
      return (
        <TextField
          key={fieldKey}
          label={label}
          value={htmlDateValue}
          onChange={(val) => {
            // Convert YYYY-MM-DD back to MM-DD-YYYY for storage
            const storedValue = formatDateToMMDDYYYY(val);
            handleFieldChange(fieldKey, storedValue);
          }}
          type="date"
          error={error}
        />
      );
    }

    // Boolean checkboxes (acknowledgments)
    if (fieldKey.includes('acknowledge') || fieldKey === 'certify_accuracy') {
      return (
        <div key={fieldKey} style={{ marginBottom: '15px' }}>
          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', justifyContent: 'flex-start' }}>
            <input
              type="checkbox"
              checked={(value as boolean) || false}
              onChange={(e) => handleFieldChange(fieldKey, e.target.checked)}
              style={{ marginRight: '8px', width: 'auto', flexShrink: 0 }}
            />
            <span style={{ textAlign: 'left' }}>{label}</span>
          </label>
          {error && (
            <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px' }}>{error}</div>
          )}
        </div>
      );
    }

    // Default to text field
    return (
      <TextField
        key={fieldKey}
        label={label}
        value={(value as string) || ''}
        onChange={(val) => handleFieldChange(fieldKey, val)}
        error={error}
      />
    );
  };

  const renderOwnerFields = (owner: Owner, ownerIndex: number) => {
    return (
      <div key={ownerIndex} style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ccc', borderRadius: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h4>Owner {ownerIndex + 1}</h4>
          {formData.owners.length > getMinOwners(formData.number_of_owners) && (
            <button
              type="button"
              onClick={() => removeOwner(ownerIndex)}
              style={{
                padding: '5px 10px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              Remove
            </button>
          )}
        </div>
        <TextField
          label="Full Legal Name"
          value={owner.full_legal_name}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'full_legal_name', val)}
        />
        <TextField
          label="Ownership Percentage"
          value={owner.ownership_percentage}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'ownership_percentage', val)}
        />
        <RadioGroupField
          label="Owner Type"
          value={owner.owner_type}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'owner_type', val)}
          options={OWNER_TYPE_OPTIONS}
        />
        <TextField
          label="Phone Number"
          value={owner.phone_number}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'phone_number', val)}
          type="tel"
        />
        <TextField
          label="Email Address"
          value={owner.email_address}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'email_address', val)}
          type="email"
        />
        <TextField
          label="Street Address 1"
          value={owner.street_address}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'street_address', val)}
        />
        <TextField
          label="Street Address 2"
          value={owner.street_address_2}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'street_address_2', val)}
        />
        <TextField
          label="City"
          value={owner.city}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'city', val)}
        />
        <SelectField
          label="State"
          value={owner.state || null}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'state', val)}
          options={STATE_OPTIONS}
          placeholder="Select a state"
        />
        <TextField
          label="ZIP Code"
          value={owner.zip_code}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'zip_code', val)}
        />
        <SelectField
          label="State of Residence"
          value={owner.state_of_residence || null}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'state_of_residence', val)}
          options={STATE_OPTIONS}
          placeholder="Select a state"
        />
        <RadioGroupField
          label="US Citizen"
          value={owner.us_citizen}
          onChange={(val) => handleOwnerFieldChange(ownerIndex, 'us_citizen', val)}
          options={YES_NO_OPTIONS}
        />
        {owner.us_citizen === 'no' && (
          <TextField
            label="Citizenship Status"
            value={owner.citizenship_status || ''}
            onChange={(val) => handleOwnerFieldChange(ownerIndex, 'citizenship_status', val)}
          />
        )}
      </div>
    );
  };

  const renderReviewStep = () => {
    return (
      <div>
        <h2 style={{ marginBottom: '30px' }}>Review Your Information</h2>
        {ENTITY_INTAKE_SECTIONS.map((section) => {
          const visibleFields = getVisibleFieldKeysForStep(section.step, formData);
          if (visibleFields.length === 0) return null;

          return (
            <div key={section.step} style={{ marginBottom: '40px' }}>
              <h3 style={{ marginBottom: '20px', paddingBottom: '10px', borderBottom: '2px solid #ccc' }}>
                {section.step}. {section.title}
              </h3>
              <div style={{ display: 'grid', gap: '15px' }}>
                {visibleFields.map((fieldKey) => {
                  if (fieldKey === 'owners') {
                    const visibleCount = getVisibleOwnerCount(formData.number_of_owners);
                    const visibleOwners = formData.owners.slice(0, visibleCount);
                    return (
                      <div key={fieldKey} style={{ marginBottom: '20px' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>Owners</div>
                        {visibleOwners.map((owner, index) => (
                          <div key={index} style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                            <div style={{ fontWeight: '600', marginBottom: '8px' }}>Owner {index + 1}</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '8px', fontSize: '14px' }}>
                              <div style={{ fontWeight: '500' }}>Full Legal Name:</div>
                              <div>{owner.full_legal_name || '(not provided)'}</div>
                              <div style={{ fontWeight: '500' }}>Ownership %:</div>
                              <div>{owner.ownership_percentage || '(not provided)'}</div>
                              <div style={{ fontWeight: '500' }}>Owner Type:</div>
                              <div>{owner.owner_type ? OWNER_TYPE_OPTIONS.find((o) => o.value === owner.owner_type)?.label || owner.owner_type : '(not provided)'}</div>
                              <div style={{ fontWeight: '500' }}>Email:</div>
                              <div>{owner.email_address || '(not provided)'}</div>
                              <div style={{ fontWeight: '500' }}>Phone:</div>
                              <div>{owner.phone_number || '(not provided)'}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  }

                  const value = formData[fieldKey];
                  const label = getFieldLabel(fieldKey);
                  let displayValue: string = '';

                  if (Array.isArray(value)) {
                    if (value.length === 0) {
                      displayValue = '(none selected)';
                    } else if (fieldKey === 'operating_states') {
                      displayValue = value.map((code) => {
                        if (code === 'nationwide') {
                          return 'Nationwide';
                        }
                        return STATE_OPTIONS.find((s) => s.value === code)?.label || code;
                      }).join(', ');
                    } else {
                      const options = getSectionOptions(section, fieldKey);
                      displayValue = value.map((val: string) => options?.find((o: Option) => o.value === val)?.label || val).join(', ');
                    }
                  } else if (typeof value === 'boolean') {
                    displayValue = value ? 'Yes' : 'No';
                  } else if (value === null || value === '') {
                    displayValue = '(not provided)';
                  } else {
                    // Try to find option label
                    const options = getSectionOptions(section, fieldKey);
                    if (options) {
                      displayValue = options.find((o: Option) => o.value === value)?.label || String(value);
                    } else if (fieldKey === 'state' || fieldKey === 'state_of_residence') {
                      displayValue = STATE_OPTIONS.find((s) => s.value === value)?.label || String(value);
                    } else if (fieldKey === 'signature_date') {
                      // Display date as MM-DD-YYYY (already stored in this format)
                      displayValue = String(value) || '(not provided)';
                    } else if (fieldKey === 'ssn_itin') {
                      // Mask SSN/ITIN for security (show only last 4 digits if provided)
                      const ssnValue = String(value || '');
                      if (ssnValue) {
                        const last4 = ssnValue.slice(-4);
                        displayValue = `***-**-${last4}`;
                      } else {
                        displayValue = '(not provided)';
                      }
                    } else {
                      displayValue = String(value);
                    }
                  }

                  return (
                    <div key={fieldKey} style={{ display: 'grid', gridTemplateColumns: '250px 1fr', gap: '10px', padding: '8px 0', borderBottom: '1px solid #eee' }}>
                      <div style={{ fontWeight: '500' }}>{label}:</div>
                      <div>{displayValue}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderFormStep = () => {
    const currentSection = ENTITY_INTAKE_SECTIONS.find((s) => s.step === currentStep);
    if (!currentSection) {
      return <div>Invalid step</div>;
    }

    const visibleFieldKeys = getVisibleFieldKeysForStep(currentStep, formData);

    return (
      <div>
        <h2 style={{ marginBottom: '30px' }}>{currentSection.title}</h2>
        <div>
          {visibleFieldKeys.map((fieldKey) => {
            // Special handling for owners array
            if (fieldKey === 'owners') {
              const maxOwners = getMaxOwners(formData.number_of_owners);
              // Show all owners that exist, up to the maximum allowed
              // Never auto-delete hidden owners - they stay in the array but are capped at max for display
              const displayOwners = formData.owners.slice(0, maxOwners);
              const canAddMore = formData.owners.length < maxOwners;

              return (
                <div key={fieldKey} style={{ marginBottom: '30px' }}>
                  <h3 style={{ marginBottom: '15px' }}>Owners</h3>
                  {displayOwners.map((owner, index) => renderOwnerFields(owner, index))}
                  {canAddMore && (
                    <button
                      type="button"
                      onClick={addOwner}
                      style={{
                        padding: '10px 20px',
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                    >
                      Add Owner
                    </button>
                  )}
                </div>
              );
            }

            const sectionOptions = ('options' in currentSection && currentSection.options) 
              ? (currentSection.options as Record<string, readonly Option[]>)
              : undefined;
            return renderField(fieldKey, sectionOptions);
          })}
        </div>
      </div>
    );
  };

  // Show loading state while draft is loading
  if (isDraftLoading) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px', textAlign: 'center' }}>
        <h2>Loading...</h2>
        <p>Loading your saved draft...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '20px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1>Business Entity Formation Intake Form</h1>
        <div style={{ marginTop: '20px', fontSize: '18px', fontWeight: '500' }}>
          {isReviewStep ? 'Review' : `Step ${currentStep} of ${totalRegularSteps}`}
        </div>
        {draftId && (
          <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
            Draft saved automatically
          </div>
        )}
      </div>

      <form onSubmit={(e) => e.preventDefault()}>
        {isReviewStep ? renderReviewStep() : renderFormStep()}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #ccc' }}>
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentStep === 1 || isSubmitting}
            style={{
              padding: '10px 20px',
              backgroundColor: currentStep === 1 ? '#ccc' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: currentStep === 1 || isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            Previous
          </button>
          <button
            type="button"
            onClick={isReviewStep ? handleSubmit : handleNext}
            disabled={isSubmitting}
            style={{
              padding: '10px 20px',
              backgroundColor: isSubmitting ? '#ccc' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
            }}
          >
            {isSubmitting ? 'Submitting...' : isReviewStep ? 'Submit' : currentStep === totalRegularSteps ? 'Review' : 'Next'}
          </button>
        </div>
      </form>
    </div>
  );
};
